/**
 * 車款選擇器：卡片 grid（照片或佔位 icon、名稱、人數/行李上限）。
 * 選項由呼叫端先依 region／包車時數過濾。
 */
import { CarFront, Check, Luggage, Users } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import type { VehicleModelOption } from "./wizard-shared";

export function VehicleModelPicker({
  models,
  selectedId,
  onSelect,
}: {
  models: VehicleModelOption[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const { t } = useLanguage();

  if (models.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground">
        {t({
          "zh-TW": "此區域／方案目前沒有可選車款，請聯繫平台。",
          "en-US": "No vehicle models available for this region/plan — please contact the platform.",
          "zh-CN": "此区域／方案目前没有可选车款，请联系平台。",
        })}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {models.map((m) => {
        const active = m.id === selectedId;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onSelect(m.id)}
            aria-pressed={active}
            className={`group relative overflow-hidden rounded-lg border text-left transition-all hover:-translate-y-px hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              active ? "border-primary bg-primary-soft/40 shadow-card" : "border-border bg-card hover:border-border-strong"
            }`}
          >
            {active && (
              <span className="absolute right-2.5 top-2.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
            {/* 車型圖一律 2.5:1（見 VEHICLE_PHOTO_RATIO 的說明）；沒照片的 fallback 也用同比例，
                免得同一列裡有照片與沒照片的卡片高度對不齊 */}
            {m.photoUrl ? (
              <img src={m.photoUrl} alt={m.name} className="aspect-[5/2] w-full border-b border-border object-cover" />
            ) : (
              <div className="flex aspect-[5/2] w-full items-center justify-center border-b border-border bg-muted/40">
                <CarFront className="h-9 w-9 text-muted-foreground/40" strokeWidth={1.25} />
              </div>
            )}
            <div className="space-y-1.5 p-3.5">
              <p className="font-display text-[15px] font-medium leading-tight">{m.name}</p>
              <p className="flex items-center gap-3 text-[12px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {t({ "zh-TW": `至多 ${m.maxPassengers} 人`, "en-US": `Up to ${m.maxPassengers} pax`, "zh-CN": `至多 ${m.maxPassengers} 人` })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Luggage className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {t({ "zh-TW": `行李 ${m.maxLuggage} 件`, "en-US": `${m.maxLuggage} bags`, "zh-CN": `行李 ${m.maxLuggage} 件` })}
                </span>
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
